import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, Phone as PhoneIcon, Mic, Image, FileText, Filter, ChevronDown } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const TYPE_ICONS = {
  audio: Mic,
  image: Image,
  video: Image,
  document: FileText,
};

const TYPE_LABELS = {
  audio: "Vocal",
  image: "Photo",
  video: "Video",
  document: "Document",
};

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (isToday) return time;
  if (isYesterday) return `Hier ${time}`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) + " " + time;
}

function MessageBubble({ msg, isOwnCompany }) {
  const TypeIcon = TYPE_ICONS[msg.message_type] || MessageSquare;
  const isVocal = msg.message_type === "audio";
  const isImage = msg.message_type === "image";
  const isVideo = msg.message_type === "video";
  const isDocument = msg.message_type === "document";
  const hasMedia = msg.media_url;
  const body = msg.body || "";

  // Extract transcription from vocal messages
  let transcription = null;
  if (isVocal && body.startsWith("[Vocal]")) {
    const match = body.match(/Transcription:\s*(.+)/s);
    transcription = match ? match[1].trim() : null;
    if (!transcription) {
      transcription = body.replace("[Vocal] ", "").trim();
    }
  }

  const mediaFullUrl = hasMedia ? `${getApiUrl()}${msg.media_url}` : null;

  return (
    <div className={`flex ${isOwnCompany ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[75%] rounded-xl px-3.5 py-2 ${
        isOwnCompany
          ? "bg-emerald-600/20 border border-emerald-500/20"
          : "bg-slate-700/50 border border-slate-600/30"
      }`}>
        {/* Sender name */}
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-semibold ${isOwnCompany ? "text-emerald-400" : "text-blue-400"}`}>
            {msg.participant_name || "Inconnu"}
          </span>
          {msg.message_type && msg.message_type !== "text" && (
            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
              isVocal ? "bg-purple-500/20 text-purple-400"
              : isImage ? "bg-blue-500/20 text-blue-400"
              : isVideo ? "bg-pink-500/20 text-pink-400"
              : isDocument ? "bg-amber-500/20 text-amber-400"
              : "bg-slate-600/50 text-slate-400"
            }`}>
              <TypeIcon size={10} />
              {TYPE_LABELS[msg.message_type] || msg.message_type}
            </span>
          )}
        </div>

        {/* Media content */}
        {isImage && mediaFullUrl ? (
          <div className="mb-1">
            <a href={mediaFullUrl} target="_blank" rel="noopener noreferrer">
              <img src={mediaFullUrl} alt="Photo WhatsApp" className="rounded-lg max-h-64 w-auto object-cover hover:opacity-90 transition-opacity cursor-pointer" loading="lazy" />
            </a>
            {body && !body.startsWith("[Image") && <p className="text-sm text-slate-200 mt-1">{body}</p>}
          </div>
        ) : isVideo && mediaFullUrl ? (
          <div className="mb-1">
            <video controls className="rounded-lg max-h-64 w-auto" preload="metadata">
              <source src={mediaFullUrl} type={msg.media_info?.content_type || "video/mp4"} />
            </video>
            {body && !body.startsWith("[Video") && <p className="text-sm text-slate-200 mt-1">{body}</p>}
          </div>
        ) : isDocument && mediaFullUrl ? (
          <a href={mediaFullUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors mb-1"
          >
            <FileText size={20} className="text-amber-400 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{msg.media_info?.original_filename || "Document"}</p>
              <p className="text-xs text-slate-400">
                {msg.media_info?.size ? `${(msg.media_info.size / 1024).toFixed(0)} KB` : "Telecharger"}
              </p>
            </div>
          </a>
        ) : isVocal ? (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-500/10 rounded-lg">
                <Mic size={14} className="text-purple-400" />
                <div className="flex gap-0.5">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="w-1 bg-purple-400/60 rounded-full" style={{ height: `${4 + Math.random() * 12}px` }} />
                  ))}
                </div>
              </div>
            </div>
            {transcription && (
              <p className="text-sm text-slate-300 italic border-l-2 border-purple-500/30 pl-2 mt-1">{transcription}</p>
            )}
          </div>
        ) : body === "[Media]" || body === "[Image]" || body === "[Video]" ? (
          <p className="text-sm text-slate-400 italic">[Media non telecharge]</p>
        ) : (
          <p className="text-sm text-slate-200 whitespace-pre-wrap break-words">{body}</p>
        )}

        {/* Timestamp */}
        <p className="text-[10px] text-slate-500 mt-1 text-right">{formatTime(msg.timestamp)}</p>
      </div>
    </div>
  );
}

function DateSeparator({ date }) {
  return (
    <div className="flex items-center gap-3 my-3">
      <div className="flex-1 h-px bg-slate-700/50" />
      <span className="text-xs text-slate-500 font-medium">{date}</span>
      <div className="flex-1 h-px bg-slate-700/50" />
    </div>
  );
}

export default function WhatsAppExchangesTab({ clientId }) {
  const [messages, setMessages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [showGroupFilter, setShowGroupFilter] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    authFetch(`${API}/whatsapp/client/${clientId}/messages`)
      .then(r => r.json())
      .then(data => {
        setMessages(data.messages || []);
        setGroups(data.groups || []);
      })
      .catch(e => console.error("Erreur WhatsApp messages:", e))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, selectedGroup]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-3 text-slate-400">
          <MessageSquare className="w-5 h-5 animate-pulse" />
          <span className="text-sm">Chargement des echanges WhatsApp...</span>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-slate-400">
        <PhoneIcon className="w-10 h-10 mb-3 opacity-30" />
        <p className="text-sm">Aucun echange WhatsApp pour ce client</p>
        <p className="text-xs text-slate-500 mt-1">Les messages apparaitront ici une fois le groupe lie au dossier</p>
      </div>
    );
  }

  const filtered = selectedGroup === "all"
    ? messages
    : messages.filter(m => m.group_id === selectedGroup);

  // Group messages by date
  let lastDate = null;
  const COMPANY_NAMES = ["SANI ADAPT", "sani adapt", "Sani Adapt"];

  const vocalsCount = filtered.filter(m => m.message_type === "audio").length;
  const textsCount = filtered.filter(m => !m.message_type || m.message_type === "text").length;

  return (
    <div className="flex flex-col h-full" data-testid="whatsapp-exchanges-tab">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-emerald-400">
            <MessageSquare size={16} />
            <span className="text-sm font-medium">{filtered.length} messages</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>{textsCount} textes</span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span>{vocalsCount} vocaux</span>
          </div>
        </div>

        {/* Group filter */}
        {groups.length > 1 && (
          <div className="relative">
            <button
              onClick={() => setShowGroupFilter(!showGroupFilter)}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-xs text-slate-300 transition-colors"
              data-testid="whatsapp-group-filter"
            >
              <Filter size={12} />
              {selectedGroup === "all" ? "Tous les groupes" : groups.find(g => g.group_id === selectedGroup)?.group_name || selectedGroup}
              <ChevronDown size={12} />
            </button>
            {showGroupFilter && (
              <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 min-w-[200px]">
                <button
                  onClick={() => { setSelectedGroup("all"); setShowGroupFilter(false); }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-700/50 transition-colors ${
                    selectedGroup === "all" ? "text-emerald-400" : "text-slate-300"
                  }`}
                >
                  Tous les groupes ({messages.length})
                </button>
                {groups.map(g => {
                  const count = messages.filter(m => m.group_id === g.group_id).length;
                  return (
                    <button
                      key={g.group_id}
                      onClick={() => { setSelectedGroup(g.group_id); setShowGroupFilter(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-700/50 transition-colors ${
                        selectedGroup === g.group_id ? "text-emerald-400" : "text-slate-300"
                      }`}
                    >
                      {g.group_name} ({count})
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5" style={{ maxHeight: "50vh" }}>
        {filtered.map((msg, idx) => {
          const msgDate = new Date(msg.timestamp).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
          const showDate = msgDate !== lastDate;
          lastDate = msgDate;
          const isOwnCompany = COMPANY_NAMES.some(n => (msg.participant_name || "").includes(n));

          return (
            <React.Fragment key={msg.message_id || idx}>
              {showDate && <DateSeparator date={msgDate} />}
              <MessageBubble msg={msg} isOwnCompany={isOwnCompany} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
